import SEO from "@/components/core/Seo";
import { GameDropsLayout } from "@/components/layout/GameDropsLayout";
import { Layout } from "@/components/layout/Layout";
import { MainContent } from "@/components/layout/MainContent";
import { createClient } from "@/prismic";
import { GetSSRPropsError } from "@/types";
import { getGameSpecs, getGamesPage } from "@/utils/cms";
import { getGameByUid } from "@/utils/cms/gameCollection";
import { logger } from "@/utils/logger";
import { notifyError } from "@/utils/monitoring";
import type { Content } from "@prismicio/client";
import { getGameDetailsExtras } from "@ui-kit/utils";
import type {
  GetServerSidePropsContext,
  InferGetServerSidePropsType,
} from "next";

export default function GameDropsPage({
  page,
  game,
  gameSpecs,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const gameExtras = getGameDetailsExtras(game);

  const pageTitle = game?.data?.name
    ? `${game?.data?.name} | Immutable Play`
    : "Immutable Play";

  return (
    <Layout screen="TwitchDrops" extras={gameExtras} userJourney="TwitchDrops">
      <SEO
        title={pageTitle}
        url={`https://play.immutable.com/twitch-drops/${game?.uid}`}
      />
      <MainContent>
        <GameDropsLayout
          defaultBgImage={page?.data?.bg_image?.url}
          game={game}
          page={page as Content.PremiumGameDetailsPageDocument}
          gameSpecs={gameSpecs.specifications}
        />
      </MainContent>
    </Layout>
  );
}

export const getServerSideProps = async (
  context: GetServerSidePropsContext,
) => {
  if (!context.preview) {
    context.res.setHeader(
      "Cache-Control",
      "public, s-maxage=1800, stale-while-revalidate=300",
    );
  }

  const uid = context.params?.uid as string;
  if (
    uid !== "hellmaster" &&
    uid !== "battlerise" &&
    uid !== "elumia" &&
    uid !== "armourx" &&
    uid !== "mtdz"
  ) {
    return {
      redirect: {
        permanent: false,
        destination: "/",
      },
    };
  }

  const client = createClient();

  try {
    if (!uid) {
      throw new Error(GetSSRPropsError.InvalidUid);
    }

    const game = await getGameByUid(client, uid);
    if (!game) {
      throw new Error(GetSSRPropsError.GameNotFound);
    }

    const page = await getGamesPage(client, uid, game?.data?.template_tier);
    if (!page) {
      throw new Error(GetSSRPropsError.PageNotFound);
    }

    const gameSpecs = getGameSpecs(game);

    return {
      props: {
        uid,
        page,
        game,
        gameSpecs,
      },
    };
  } catch (error) {
    console.error(error, { uid });
    logger.error("gameTwitchDropsPageStaticProps", { uid });
    notifyError(error, "gameTwitchDropsPageStaticProps", { uid });

    // Pages not found in the CMS can be handled as 404 pages.
    if (
      error instanceof Error &&
      error.message === GetSSRPropsError.PageNotFound
    ) {
      return { notFound: true };
    }

    // Any other error should be rethrown to abort the build process.
    // This is to ensure we don't silently return 404 for pages that do exist.
    throw error;
  }
};
